import { useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";
import { useLocalizedNavigate } from "lib/i18n/navigation";

import { assistantName, isRequestWaiting } from "./site-assistant";
import { SiteLayout, SiteLoadingCard } from "./site-layout";
import { useAnswerSiteAssistant, useMySiteAssistant } from "./site.hooks";
import { notifyError } from "./site.notify";
import { SITE_ROUTE } from "./site.routes";
import { useSiteTranslations } from "./use-site-translations";

function AssistantRequest() {
  const { t } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  const { data, loading } = useMySiteAssistant();
  const [answerRequest, { loading: isAnswering }] = useAnswerSiteAssistant();
  const [answer, setAnswer] = useState<{ allow: boolean; client: string } | null>(null);
  const request = data?.mySiteAssistant.pendingRequest ?? null;
  const client = answer?.client ?? assistantName(request?.clientName);
  const close = () => navigate(SITE_ROUTE);

  async function onAnswer(allow: boolean) {
    if (!request) return;
    try {
      await answerRequest({ variables: { requestUuid: request.uuid, allow } });
      setAnswer({ allow, client });
    } catch {
      notifyError(t.assistant.request.none, t.errors.tryAgain);
    }
  }

  if (!answer && loading && !data) {
    return <SiteLoadingCard />;
  }

  return (
    <SoftBox display="flex" justifyContent="center">
      <Card sx={{ width: "100%", maxWidth: "36rem" }} data-testid="site-assistant-request-screen">
        <SoftBox p={4} display="flex" flexDirection="column" gap={2} textAlign="center">
          {answer ? (
            <>
              {answer.allow ? (
                <SoftBox display="flex" justifyContent="center">
                  <Icon color="success" sx={{ fontSize: "3rem !important" }}>
                    check_circle
                  </Icon>
                </SoftBox>
              ) : null}
              <SoftTypography variant="h5">
                {interpolateTemplate(
                  answer.allow ? t.assistant.request.allowedTitle : t.assistant.request.deniedTitle,
                  { client }
                )}
              </SoftTypography>
              <SoftTypography variant="button" color="text" fontWeight="regular">
                {interpolateTemplate(
                  answer.allow
                    ? t.assistant.request.allowedMessage
                    : t.assistant.request.deniedMessage,
                  { client }
                )}
              </SoftTypography>
              <SoftButton
                variant="outlined"
                color="info"
                onClick={close}
                data-testid="site-assistant-request-close"
              >
                {answer.allow ? t.assistant.request.done : t.assistant.request.close}
              </SoftButton>
            </>
          ) : !isRequestWaiting(request) ? (
            <>
              <SoftTypography variant="button" color="text" fontWeight="regular">
                {t.assistant.request.none}
              </SoftTypography>
              <SoftButton variant="outlined" color="info" onClick={close}>
                {t.assistant.request.close}
              </SoftButton>
            </>
          ) : (
            <>
              <SoftTypography variant="h5">
                {interpolateTemplate(t.assistant.request.title, { client })}
              </SoftTypography>
              <SoftBox textAlign="left" border="1px solid" borderColor="grey.300" borderRadius="lg">
                <SoftBox display="flex" gap={1.5} px={2} py={1.5}>
                  <Icon color="success">check</Icon>
                  <SoftTypography variant="button" fontWeight="regular">
                    {t.assistant.request.can}
                  </SoftTypography>
                </SoftBox>
                <Divider sx={{ my: 0 }} />
                <SoftBox display="flex" gap={1.5} px={2} py={1.5}>
                  <Icon color="error">close</Icon>
                  <SoftTypography variant="button" fontWeight="regular">
                    {t.assistant.request.cannot}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                {t.assistant.request.note}
              </SoftTypography>
              <SoftButton
                variant="gradient"
                color="info"
                size="large"
                disabled={isAnswering}
                onClick={() => void onAnswer(true)}
                data-testid="site-assistant-request-allow"
              >
                {isAnswering ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  t.assistant.request.allow
                )}
              </SoftButton>
              <SoftButton
                variant="text"
                color="secondary"
                disabled={isAnswering}
                onClick={() => void onAnswer(false)}
                data-testid="site-assistant-request-deny"
              >
                {t.assistant.request.deny}
              </SoftButton>
            </>
          )}
        </SoftBox>
      </Card>
    </SoftBox>
  );
}

// "Дозволити Claude редагувати ваш сайт?": the Provider answers the request their "Підключити
// Claude" link made.
export function SiteAssistantRequestPage() {
  return <SiteLayout>{() => <AssistantRequest />}</SiteLayout>;
}
